import React from 'react';
import {
  BanknotesIcon,
  BeakerIcon,
  BellIcon,
  BookOpenIcon,
  BuildingLibraryIcon,
  CalendarDaysIcon,
  ChartBarIcon,
  ClipboardDocumentListIcon,
  Cog6ToothIcon,
  CreditCardIcon,
  DocumentTextIcon,
  HomeIcon,
  InboxIcon,
  MegaphoneIcon,
  PencilSquareIcon,
  PhotoIcon,
  SparklesIcon,
  UserCircleIcon,
  UserGroupIcon,
  UserIcon,
  UsersIcon,
  WrenchScrewdriverIcon,
  DevicePhoneMobileIcon,
  HeartIcon,
  DocumentChartBarIcon,
  XMarkIcon,
  LockClosedIcon,
  EyeIcon,
  GiftIcon,
  ShieldCheckIcon,
  MusicalNoteIcon,
  CameraIcon
} from '@heroicons/react/24/outline';
import HorseSilhouetteIcon from '../components/common/HorseSilhouetteIcon.tsx';

export type AppIconName =
  | 'home'
  | 'families'
  | 'ranchGirl'
  | 'horse'
  | 'horseReports'
  | 'board'
  | 'members'
  | 'duties'
  | 'events'
  | 'notifications'
  | 'girlsCorner'
  | 'textMessage'
  | 'documents'
  | 'invoices'
  | 'reports'
  | 'userActivity'
  | 'adminUsers'
  | 'settings'
  | 'bell'
  | 'directory'
  | 'monthlyDues'
  | 'profile'
  | 'medical'
  | 'farrier'
  | 'info'
  | 'edit'
  | 'health'
  | 'care'
  | 'notes'
  | 'clipboard'
  | 'emptyInbox'
  | 'emptyDocs'
  | 'emptyChart'
  | 'emptyMoney'
  | 'photo'
  | 'payment'
  | 'lesson'
  | 'membership'
  | 'donation'
  | 'close'
  | 'lock'
  | 'impersonate'
  | 'search'
  | 'other'
  | 'form'
  | 'event'
  | 'equipment'
  | 'camera';

type IconComponent = React.ComponentType<React.SVGProps<SVGSVGElement>>;

const ICONS: Record<AppIconName, IconComponent> = {
  home: HomeIcon,
  families: UserGroupIcon,
  ranchGirl: UserIcon,
  horse: HorseSilhouetteIcon,
  horseReports: DocumentChartBarIcon,
  board: BuildingLibraryIcon,
  members: UsersIcon,
  duties: ClipboardDocumentListIcon,
  events: CalendarDaysIcon,
  notifications: MegaphoneIcon,
  girlsCorner: SparklesIcon,
  textMessage: DevicePhoneMobileIcon,
  documents: DocumentTextIcon,
  invoices: BanknotesIcon,
  reports: ChartBarIcon,
  userActivity: ChartBarIcon,
  adminUsers: UsersIcon,
  settings: Cog6ToothIcon,
  bell: BellIcon,
  directory: BookOpenIcon,
  monthlyDues: CreditCardIcon,
  profile: UserCircleIcon,
  medical: BeakerIcon,
  farrier: WrenchScrewdriverIcon,
  info: ClipboardDocumentListIcon,
  edit: PencilSquareIcon,
  health: HeartIcon,
  care: HeartIcon,
  notes: DocumentTextIcon,
  clipboard: ClipboardDocumentListIcon,
  emptyInbox: InboxIcon,
  emptyDocs: DocumentTextIcon,
  emptyChart: ChartBarIcon,
  emptyMoney: BanknotesIcon,
  photo: PhotoIcon,
  payment: CreditCardIcon,
  lesson: HorseSilhouetteIcon,
  membership: UserGroupIcon,
  donation: GiftIcon,
  close: XMarkIcon,
  lock: LockClosedIcon,
  impersonate: EyeIcon,
  search: EyeIcon,
  other: ClipboardDocumentListIcon,
  form: ClipboardDocumentListIcon,
  event: MusicalNoteIcon,
  equipment: ShieldCheckIcon,
  camera: CameraIcon
};

export function AppIcon({
  name,
  className = 'w-5 h-5',
  strokeWidth = 1.75
}: {
  name: AppIconName;
  className?: string;
  strokeWidth?: number;
}) {
  const Icon = ICONS[name] || DocumentTextIcon;
  return <Icon className={className} strokeWidth={strokeWidth} aria-hidden />;
}

export function SidebarNavIcon({ name, active }: { name: AppIconName; active: boolean }) {
  return (
    <AppIcon
      name={name}
      className={`w-5 h-5 shrink-0 ${active ? 'text-white' : 'text-gray-500 group-hover:text-gray-700'}`}
    />
  );
}

export function notificationTypeIcon(type: string): AppIconName {
  switch (type) {
    case 'horse':
      return 'horse';
    case 'event':
      return 'events';
    case 'invoice':
    case 'payment':
      return 'invoices';
    case 'grounds':
      return 'home';
    case 'maintenance':
      return 'farrier';
    case 'urgent':
      return 'bell';
    case 'general':
      return 'notifications';
    default:
      return 'bell';
  }
}

export function invoiceTypeIcon(type: string): AppIconName {
  switch (type) {
    case 'membership':
      return 'membership';
    case 'lesson':
      return 'lesson';
    case 'event':
      return 'event';
    case 'equipment':
      return 'equipment';
    case 'donation':
      return 'donation';
    default:
      return 'other';
  }
}

export function documentCategoryIcon(category: string): AppIconName {
  switch (category) {
    case 'form':
      return 'form';
    case 'medical':
      return 'medical';
    case 'announcement':
      return 'notifications';
    case 'lesson_plan':
      return 'directory';
    default:
      return 'documents';
  }
}

export function documentShareIcon(shareType: string): AppIconName {
  if (shareType === 'all_families') return 'families';
  return 'members';
}
